import { z } from 'zod'

/**
 * Item ids, a closed enum shared with the sim (`ItemId`): Phase 1's five, then PHASE3 T3.12's
 * three. The order is fixed: it is the order `rollItem` walks the weight rows in, so changing it
 * changes every roll.
 */
export const ITEM_IDS = [
  'boost_jet',
  'shield_bubble',
  'trip_wire',
  'spike_ball',
  'homing_drone',
  'oil_slick',
  'leader_missile',
  'magnet',
] as const

export type ItemId = (typeof ITEM_IDS)[number]

const ms = z.number().int().positive()
const px = z.number().int().positive()
const speed = z.number().positive()
const weight = z.number().int().min(0)

/** One weight per item id, every id present, nothing else (D-022). */
const weightRow = z
  .strictObject({
    boost_jet: weight,
    shield_bubble: weight,
    trip_wire: weight,
    spike_ball: weight,
    homing_drone: weight,
    oil_slick: weight,
    leader_missile: weight,
    magnet: weight,
  } satisfies Record<ItemId, typeof weight>)
  .refine((row) => ITEM_IDS.reduce((sum, id) => sum + row[id], 0) > 0, {
    message: 'weights must sum to more than 0',
  })

/** Raw `items.json` as authored (T1.3): units live in the key name. */
export const itemsFileSchema = z.strictObject({
  itemCooldownMs: z.number().int().min(0),
  crateRespawnMs: ms,
  shieldPopInvulnMs: z.number().int().min(0),
  crateSizePx: px,
  coinSizePx: px,
  boost_jet: z.strictObject({ speedPct: z.number().min(0).max(200), durationMs: ms }),
  shield_bubble: z.strictObject({ durationMs: ms }),
  trip_wire: z.strictObject({
    stunMs: ms,
    ownerGraceMs: z.number().int().min(0),
    lifetimeMs: ms,
    dropBehindPx: z.number().int().min(0),
    widthPx: px,
    heightPx: px,
  }),
  spike_ball: z.strictObject({ stunMs: ms, speedPxPerSec: speed, lifetimeMs: ms, sizePx: px }),
  homing_drone: z.strictObject({
    stunMs: ms,
    speedPxPerSec: speed,
    verticalSpeedPxPerSec: speed,
    lockPx: z.number().min(0),
    lifetimeMs: ms,
    sizePx: px,
    /** Aim point below the target's top, as % of the standing height. */
    aimPct: z.number().min(0).max(100),
  }),
  /** Trap: a skid (no control, `skidSpeedPct` of base speed) instead of a stun; jump over it. */
  oil_slick: z.strictObject({
    skidMs: ms,
    skidSpeedPct: z.number().min(0).max(100),
    ownerGraceMs: z.number().int().min(0),
    lifetimeMs: ms,
    dropBehindPx: z.number().int().min(0),
    widthPx: px,
    heightPx: px,
  }),
  /** Flies to the current leader; the blast stuns it and, less, racers within the radius. */
  leader_missile: z.strictObject({
    stunMs: ms,
    splashStunMs: ms,
    splashRadiusPx: px,
    speedPxPerSec: speed,
    lifetimeMs: ms,
    sizePx: px,
  }),
  /** Self: shards and item boxes within `radiusPx` of the runner are collected. */
  magnet: z.strictObject({ durationMs: ms, radiusPx: px }),
  /** Rows keyed by live rank (1 = leading). The Leader Missile is a last-place item. */
  weightsByRank: z
    .strictObject({ '1': weightRow, '2': weightRow, '3': weightRow, '4': weightRow })
    .refine((r) => r['1'].leader_missile + r['2'].leader_missile + r['3'].leader_missile === 0, {
      message: 'leader_missile is rolled by rank 4 only (GAME_PLAN §2.4)',
    }),
})

export type ItemsFile = z.infer<typeof itemsFileSchema>
